import AdminPageBackground from "@/components/admin/AdminPageBackground";
import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Globe, Plus, Search, Twitter, Calendar, Clock, Link as LinkIcon, Trash2, Edit, Loader2, Users, Mic, Bell, CalendarClock, Upload, X, Radio, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

interface XSpaceDB {
  id: string;
  event_id: number;
  title: string;
  title_ar: string | null;
  x_space_url: string | null;
  scheduled_at: string;
  created_at: string;
  updated_at: string;
  reminder_30min_sent: boolean;
  reminder_10min_sent: boolean;
  image_url: string | null;
  description: string | null;
  description_ar: string | null;
  end_time: string | null;
  attendees: number | null;
  is_live: boolean | null;
}

const XSpacesAdmin = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const queryClient = useQueryClient();
  
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSpace, setSelectedSpace] = useState<XSpaceDB | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [fetchedInfo, setFetchedInfo] = useState<any>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isUploadingEditImage, setIsUploadingEditImage] = useState(false);
  const [isUpdatingAttendees, setIsUpdatingAttendees] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editFileInputRef = useRef<HTMLInputElement>(null);
  
  const [newSpace, setNewSpace] = useState({
    title: "",
    title_ar: "",
    scheduled_at: "",
    end_time: "",
    x_space_url: "",
    description: "",
    description_ar: "",
    image_url: "/images/x-spaces-default.jpg",
    attendees: 0,
    is_live: false,
    enableReminders: true,
  });

  // Fetch X Spaces from database
  const { data: xSpaces = [], isLoading } = useQuery({
    queryKey: ["admin-x-spaces"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("scheduled_x_spaces")
        .select("*")
        .order("scheduled_at", { ascending: false });
      
      if (error) throw error;
      return data as XSpaceDB[];
    }
  });

  // Filter spaces based on search
  const filteredSpaces = xSpaces.filter((space) => {
    const title = isRTL ? (space.title_ar || space.title) : space.title;
    return title.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Add mutation
  const addMutation = useMutation({
    mutationFn: async (spaceData: typeof newSpace) => {
      const nextEventId = xSpaces.length > 0 
        ? Math.max(...xSpaces.map(s => s.event_id)) + 1 
        : 1;

      const { data, error } = await supabase
        .from("scheduled_x_spaces")
        .insert({
          event_id: nextEventId,
          title: spaceData.title,
          title_ar: spaceData.title_ar || null,
          scheduled_at: spaceData.scheduled_at,
          end_time: spaceData.end_time || null,
          x_space_url: spaceData.x_space_url || null,
          description: spaceData.description || null,
          description_ar: spaceData.description_ar || null,
          image_url: spaceData.image_url !== "/images/x-spaces-default.jpg" ? spaceData.image_url : null,
          attendees: spaceData.attendees || 0,
          is_live: spaceData.is_live || false,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-x-spaces"] });
      queryClient.invalidateQueries({ queryKey: ["x-spaces"] });
      queryClient.invalidateQueries({ queryKey: ["x-spaces-upcoming"] });
      toast.success(isRTL ? "تم إضافة المساحة بنجاح" : "Space added successfully");
      setIsAddDialogOpen(false);
      resetNewSpace();
    },
    onError: (error) => {
      console.error("Error adding space:", error);
      toast.error(isRTL ? "حدث خطأ أثناء الإضافة" : "Error adding space");
    }
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (space: XSpaceDB) => {
      const { error } = await supabase
        .from("scheduled_x_spaces")
        .update({
          title: space.title,
          title_ar: space.title_ar,
          scheduled_at: space.scheduled_at,
          end_time: space.end_time,
          x_space_url: space.x_space_url,
          description: space.description,
          description_ar: space.description_ar,
          image_url: space.image_url !== "/images/x-spaces-default.jpg" ? space.image_url : null,
          attendees: space.attendees || 0,
          is_live: space.is_live || false,
        })
        .eq("id", space.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-x-spaces"] });
      queryClient.invalidateQueries({ queryKey: ["x-spaces"] });
      queryClient.invalidateQueries({ queryKey: ["x-spaces-upcoming"] });
      toast.success(isRTL ? "تم تحديث المساحة بنجاح" : "Space updated successfully");
      setIsEditDialogOpen(false);
      setSelectedSpace(null);
    },
    onError: (error) => {
      console.error("Error updating space:", error);
      toast.error(isRTL ? "حدث خطأ أثناء التحديث" : "Error updating space");
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("scheduled_x_spaces")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-x-spaces"] });
      queryClient.invalidateQueries({ queryKey: ["x-spaces"] });
      queryClient.invalidateQueries({ queryKey: ["x-spaces-upcoming"] });
      toast.success(isRTL ? "تم حذف المساحة بنجاح" : "Space deleted successfully");
    },
    onError: (error) => {
      console.error("Error deleting space:", error);
      toast.error(isRTL ? "حدث خطأ أثناء الحذف" : "Error deleting space");
    }
  });

  const resetNewSpace = () => {
    setNewSpace({
      title: "",
      title_ar: "",
      scheduled_at: "",
      end_time: "",
      x_space_url: "",
      description: "",
      description_ar: "",
      image_url: "/images/x-spaces-default.jpg",
      attendees: 0,
      is_live: false,
      enableReminders: true,
    });
    setFetchedInfo(null);
  };

  const handleEdit = (space: XSpaceDB) => {
    setSelectedSpace({ ...space });
    setIsEditDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const handleUpdate = () => {
    if (selectedSpace) {
      updateMutation.mutate(selectedSpace);
    }
  };

  const handleAddSpace = () => {
    if (!newSpace.title || !newSpace.scheduled_at) {
      toast.error(isRTL ? "يرجى ملء العنوان والتاريخ" : "Please fill title and date");
      return;
    }
    addMutation.mutate(newSpace);
  };

  // Upload image to Supabase Storage
  const uploadImage = async (file: File, isEdit: boolean = false): Promise<string | null> => {
    if (isEdit) {
      setIsUploadingEditImage(true);
    } else {
      setIsUploadingImage(true);
    }

    try {
      if (!file.type.startsWith('image/')) {
        toast.error(isRTL ? "الرجاء اختيار ملف صورة صالح" : "Please select a valid image file");
        return null;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error(isRTL ? "حجم الصورة يجب أن يكون أقل من 5 ميجابايت" : "Image size must be less than 5MB");
        return null;
      }

      const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('x-spaces-images')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error("Upload error:", uploadError);
        toast.error(isRTL ? "فشل في رفع الصورة" : "Failed to upload image");
        return null;
      }

      const { data: urlData } = supabase.storage
        .from('x-spaces-images')
        .getPublicUrl(fileName);

      toast.success(isRTL ? "تم رفع الصورة بنجاح" : "Image uploaded successfully");
      return urlData.publicUrl;

    } catch (error) {
      console.error("Upload error:", error);
      toast.error(isRTL ? "حدث خطأ أثناء رفع الصورة" : "Error uploading image");
      return null;
    } finally {
      if (isEdit) {
        setIsUploadingEditImage(false);
      } else {
        setIsUploadingImage(false);
      }
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const imageUrl = await uploadImage(file, false);
    if (imageUrl) {
      setNewSpace(prev => ({ ...prev, image_url: imageUrl }));
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleEditImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const imageUrl = await uploadImage(file, true);
    if (imageUrl && selectedSpace) {
      setSelectedSpace({ ...selectedSpace, image_url: imageUrl });
    }
    
    if (editFileInputRef.current) {
      editFileInputRef.current.value = '';
    }
  };

  const removeImage = () => {
    setNewSpace(prev => ({ ...prev, image_url: "/images/x-spaces-default.jpg" }));
  };

  const removeEditImage = () => {
    if (selectedSpace) {
      setSelectedSpace({ ...selectedSpace, image_url: "/images/x-spaces-default.jpg" });
    }
  };

  const fetchSpaceInfo = async (url: string) => {
    if (!url || (!url.includes("twitter.com/i/spaces") && !url.includes("x.com/i/spaces"))) {
      toast.error(isRTL ? "الرجاء إدخال رابط مساحة X صحيح" : "Please enter a valid X Space URL");
      return;
    }

    setIsFetching(true);
    setFetchedInfo(null);

    try {
      const { data, error } = await supabase.functions.invoke('fetch-x-space-info', {
        body: { spaceUrl: url }
      });

      if (data?.rateLimited) {
        toast.warning(isRTL 
          ? "تم تجاوز حد الطلبات. يرجى إدخال البيانات يدوياً" 
          : "Rate limit reached. Please enter details manually");
        setNewSpace(prev => ({ ...prev, x_space_url: url }));
        return;
      }

      if (error) throw error;

      if (data?.error && !data?.title) {
        toast.warning(isRTL 
          ? "لم نتمكن من جلب البيانات. يمكنك إدخالها يدوياً" 
          : "Could not fetch data. You can enter manually");
        setNewSpace(prev => ({ ...prev, x_space_url: url }));
        return;
      }

      setFetchedInfo(data);

      if (data?.title) {
        setNewSpace(prev => ({
          ...prev,
          title: data.title,
          title_ar: data.title,
          x_space_url: url,
          image_url: data.scrapedImage || data.defaultImage || "/images/x-spaces-default.jpg",
          attendees: data.participantCount || 0,
          description: data.scrapedDescription || "",
          description_ar: data.scrapedDescription || "",
        }));
      }

      if (data?.scheduledStart) {
        setNewSpace(prev => ({
          ...prev,
          scheduled_at: data.scheduledStart,
        }));
      }

      toast.success(isRTL ? "تم جلب بيانات المساحة بنجاح" : "Space info fetched successfully");

    } catch (error: any) {
      console.error("Error fetching space info:", error);
      toast.warning(isRTL 
        ? "لم نتمكن من جلب البيانات. يمكنك إدخالها يدوياً" 
        : "Could not fetch data. You can enter manually");
      setNewSpace(prev => ({ ...prev, x_space_url: url }));
    } finally {
      setIsFetching(false);
    }
  };

  const updateAllAttendeeCounts = async () => {
    setIsUpdatingAttendees(true);
    try {
      const { data, error } = await supabase.functions.invoke('update-spaces-attendees');
      
      if (error) throw error;
      
      if (data?.rateLimitHit) {
        toast.warning(isRTL 
          ? `تم تحديث ${data.updated} مساحات قبل الوصول لحد الطلبات` 
          : `Updated ${data.updated} spaces before hitting rate limit`);
      } else {
        toast.success(isRTL 
          ? `تم تحديث ${data.updated} من ${data.totalSpaces} مساحات` 
          : `Updated ${data.updated} of ${data.totalSpaces} spaces`);
      }
      
      // Refresh the data
      queryClient.invalidateQueries({ queryKey: ["admin-x-spaces"] });
      queryClient.invalidateQueries({ queryKey: ["x-spaces"] });
      
    } catch (error: any) {
      console.error("Error updating attendees:", error);
      toast.error(isRTL ? "حدث خطأ أثناء التحديث" : "Error updating attendees");
    } finally {
      setIsUpdatingAttendees(false);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatTime = (dateStr: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleTimeString(isRTL ? 'ar-SA' : 'en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <AdminPageBackground>
      <nav className="border-b border-white/10 bg-white/5 backdrop-blur-sm w-full">
        <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/admin-dashboard"
              className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              {isRTL ? "العودة للوحة التحكم" : "Back to Dashboard"}
            </Link>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => i18n.changeLanguage(i18n.language === "en" ? "ar" : "en")}
              className="rounded-full"
            >
              <Globe className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </nav>

      <main className={`flex-1 w-full bg-background ${isRTL ? "rtl" : "ltr"}`}>
        <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gray-100 rounded-lg">
                <Twitter className="h-6 w-6 text-gray-900" />
              </div>
              <h1 className="text-3xl font-bold">
                {isRTL ? "إدارة مساحات X" : "X Spaces Management"}
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline"
                className="flex items-center gap-2"
                onClick={updateAllAttendeeCounts}
                disabled={isUpdatingAttendees}
              >
                {isUpdatingAttendees ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                {isRTL ? "تحديث المشاركين" : "Update Attendees"}
              </Button>
              <Button 
                className="flex items-center gap-2"
                onClick={() => setIsAddDialogOpen(true)}
              >
                <Plus className="w-4 h-4 shrink-0" />
                {isRTL ? "إضافة مساحة" : "Add Space"}
              </Button>
            </div>
          </div>

          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="search"
                placeholder={isRTL ? "البحث في المساحات..." : "Search spaces..."}
                className="pl-10 pr-4"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filteredSpaces.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm p-8 text-center">
              <Twitter className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-700 mb-2">
                {isRTL ? "لا توجد مساحات" : "No Spaces Found"}
              </h2>
              <p className="text-gray-500 mb-4">
                {isRTL ? "ابدأ بإضافة مساحة جديدة" : "Start by adding a new space"}
              </p>
              <Button onClick={() => setIsAddDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                {isRTL ? "إضافة مساحة" : "Add Space"}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredSpaces.map((space) => (
                <div key={space.id} className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                  <div className="h-40 relative">
                    <img 
                      src={space.image_url || "/images/x-spaces-default.jpg"} 
                      alt={isRTL ? (space.title_ar || space.title) : space.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = "/images/x-spaces-default.jpg";
                      }}
                    />
                    <div className="absolute top-3 right-3 bg-gray-900 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1">
                      <Twitter className="w-3 h-3" />
                      X Space
                    </div>
                    {space.is_live && (
                      <div className="absolute top-3 left-3 bg-red-600 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1 animate-pulse">
                        <Radio className="w-3 h-3" />
                        {isRTL ? "بث مباشر" : "LIVE"}
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="text-lg font-semibold mb-2">
                      {isRTL ? (space.title_ar || space.title) : space.title}
                    </h3>
                    <div className="space-y-2 mb-4 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(space.scheduled_at)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>{formatTime(space.scheduled_at)}</span>
                      </div>
                      {space.x_space_url && (
                        <div className="flex items-center gap-2">
                          <LinkIcon className="w-4 h-4" />
                          <a 
                            href={space.x_space_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary hover:underline truncate"
                          >
                            {space.x_space_url}
                          </a>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => handleEdit(space)}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        {isRTL ? "تعديل" : "Edit"}
                      </Button>
                      <Button 
                        variant="destructive" 
                        size="icon"
                        onClick={() => handleDelete(space.id)}
                        disabled={deleteMutation.isPending}
                      >
                        {deleteMutation.isPending ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isRTL ? "تعديل المساحة" : "Edit Space"}</DialogTitle>
          </DialogHeader>
          {selectedSpace && (
            <div className="space-y-4 py-4">
              {/* Image Upload Section */}
              <div>
                <label className="text-sm font-medium mb-2 block">
                  {isRTL ? "صورة المساحة" : "Space Image"}
                </label>
                <div className="relative h-32 rounded-lg overflow-hidden bg-muted mb-2">
                  <img 
                    src={selectedSpace.image_url || "/images/x-spaces-default.jpg"} 
                    alt="Space preview" 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = "/images/x-spaces-default.jpg";
                    }}
                  />
                  {selectedSpace.image_url && selectedSpace.image_url !== "/images/x-spaces-default.jpg" && (
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2 h-6 w-6"
                      onClick={removeEditImage}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <input
                    type="file"
                    ref={editFileInputRef}
                    onChange={handleEditImageUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => editFileInputRef.current?.click()}
                    disabled={isUploadingEditImage}
                    className="flex-1"
                  >
                    {isUploadingEditImage ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {isRTL ? "جاري الرفع..." : "Uploading..."}
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        {isRTL ? "رفع صورة" : "Upload Image"}
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    {isRTL ? "العنوان (إنجليزي)" : "Title (English)"}
                  </label>
                  <Input
                    value={selectedSpace.title || ""}
                    onChange={(e) => setSelectedSpace({
                      ...selectedSpace,
                      title: e.target.value
                    })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    {isRTL ? "العنوان (عربي)" : "Title (Arabic)"}
                  </label>
                  <Input
                    value={selectedSpace.title_ar || ""}
                    onChange={(e) => setSelectedSpace({
                      ...selectedSpace,
                      title_ar: e.target.value
                    })}
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-1 block">
                  {isRTL ? "تاريخ ووقت البدء" : "Start Date & Time"}
                </label>
                <Input
                  type="datetime-local"
                  value={selectedSpace.scheduled_at ? selectedSpace.scheduled_at.slice(0, 16) : ""}
                  onChange={(e) => setSelectedSpace({
                    ...selectedSpace,
                    scheduled_at: e.target.value
                  })}
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">
                  {isRTL ? "رابط مساحة X" : "X Space URL"}
                </label>
                <Input
                  value={selectedSpace.x_space_url || ""}
                  onChange={(e) => setSelectedSpace({
                    ...selectedSpace,
                    x_space_url: e.target.value
                  })}
                  placeholder="https://x.com/i/spaces/..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    {isRTL ? "الوصف (إنجليزي)" : "Description (English)"}
                  </label>
                  <Textarea
                    value={selectedSpace.description || ""}
                    onChange={(e) => setSelectedSpace({
                      ...selectedSpace,
                      description: e.target.value
                    })}
                    rows={3}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    {isRTL ? "الوصف (عربي)" : "Description (Arabic)"}
                  </label>
                  <Textarea
                    value={selectedSpace.description_ar || ""}
                    onChange={(e) => setSelectedSpace({
                      ...selectedSpace,
                      description_ar: e.target.value
                    })}
                    rows={3}
                  />
                </div>
              </div>

              {/* Live Broadcast Toggle */}
              <div className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <Radio className="w-4 h-4 text-red-600" />
                  <div>
                    <p className="text-sm font-medium">
                      {isRTL ? "بث مباشر الآن" : "Live Broadcast Now"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {isRTL 
                        ? "تفعيل هذا سيظهر شارة 'بث مباشر'" 
                        : "Enable to show 'LIVE' badge"}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={selectedSpace.is_live || false}
                  onCheckedChange={(checked) => setSelectedSpace({
                    ...selectedSpace,
                    is_live: checked
                  })}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                  {isRTL ? "إلغاء" : "Cancel"}
                </Button>
                <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {isRTL ? "جاري الحفظ..." : "Saving..."}
                    </>
                  ) : (
                    isRTL ? "حفظ التغييرات" : "Save Changes"
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={(open) => {
        setIsAddDialogOpen(open);
        if (!open) resetNewSpace();
      }}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isRTL ? "إضافة مساحة جديدة" : "Add New Space"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* URL Input with Fetch Button */}
            <div>
              <label className="text-sm font-medium mb-1 block">
                {isRTL ? "رابط مساحة X" : "X Space URL"}
              </label>
              <div className="flex gap-2">
                <Input
                  value={newSpace.x_space_url}
                  onChange={(e) => setNewSpace({
                    ...newSpace,
                    x_space_url: e.target.value
                  })}
                  placeholder="https://x.com/i/spaces/..."
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => fetchSpaceInfo(newSpace.x_space_url)}
                  disabled={isFetching || !newSpace.x_space_url}
                >
                  {isFetching ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {isRTL ? "جاري الجلب..." : "Fetching..."}
                    </>
                  ) : (
                    isRTL ? "جلب البيانات" : "Fetch Info"
                  )}
                </Button>
              </div>
            </div>

            {/* Fetched Info Display */}
            {fetchedInfo && (
              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Twitter className="w-4 h-4" />
                  <span className="font-medium">{isRTL ? "معلومات المساحة" : "Space Info"}</span>
                </div>
                {fetchedInfo.title && (
                  <p className="text-sm"><strong>{isRTL ? "العنوان:" : "Title:"}</strong> {fetchedInfo.title}</p>
                )}
                {fetchedInfo.participantCount > 0 && (
                  <span className="flex items-center gap-1 text-sm">
                    <Users className="w-4 h-4" />
                    {fetchedInfo.participantCount} {isRTL ? "مشارك" : "participants"}
                  </span>
                )}
              </div>
            )}

            {/* Image Preview & Upload */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                {isRTL ? "صورة المساحة" : "Space Image"}
              </label>
              <div className="relative h-32 rounded-lg overflow-hidden bg-muted mb-2">
                <img 
                  src={newSpace.image_url || "/images/x-spaces-default.jpg"} 
                  alt="Space preview" 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = "/images/x-spaces-default.jpg";
                  }}
                />
                {newSpace.image_url && newSpace.image_url !== "/images/x-spaces-default.jpg" && (
                  <Button
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2 h-6 w-6"
                    onClick={removeImage}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/*"
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingImage}
                  className="flex-1"
                >
                  {isUploadingImage ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {isRTL ? "جاري الرفع..." : "Uploading..."}
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      {isRTL ? "رفع صورة" : "Upload Image"}
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Title Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">
                  {isRTL ? "العنوان (إنجليزي)" : "Title (English)"}
                </label>
                <Input
                  value={newSpace.title}
                  onChange={(e) => setNewSpace({
                    ...newSpace,
                    title: e.target.value
                  })}
                  placeholder={isRTL ? "عنوان المساحة" : "Space title"}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">
                  {isRTL ? "العنوان (عربي)" : "Title (Arabic)"}
                </label>
                <Input
                  value={newSpace.title_ar}
                  onChange={(e) => setNewSpace({
                    ...newSpace,
                    title_ar: e.target.value
                  })}
                  placeholder={isRTL ? "عنوان المساحة بالعربي" : "Arabic title"}
                />
              </div>
            </div>

            {/* Date Time */}
            <div>
              <label className="text-sm font-medium mb-1 block">
                {isRTL ? "تاريخ ووقت البدء" : "Start Date & Time"}
              </label>
              <Input
                type="datetime-local"
                value={newSpace.scheduled_at ? newSpace.scheduled_at.slice(0, 16) : ""}
                onChange={(e) => setNewSpace({
                  ...newSpace,
                  scheduled_at: e.target.value
                })}
              />
            </div>

            {/* Description Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">
                  {isRTL ? "الوصف (إنجليزي)" : "Description (English)"}
                </label>
                <Textarea
                  value={newSpace.description}
                  onChange={(e) => setNewSpace({
                    ...newSpace,
                    description: e.target.value
                  })}
                  rows={3}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">
                  {isRTL ? "الوصف (عربي)" : "Description (Arabic)"}
                </label>
                <Textarea
                  value={newSpace.description_ar}
                  onChange={(e) => setNewSpace({
                    ...newSpace,
                    description_ar: e.target.value
                  })}
                  rows={3}
                />
              </div>
            </div>

            {/* Reminders Toggle */}
            <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-blue-600" />
                <div>
                  <p className="text-sm font-medium">
                    {isRTL ? "تفعيل التذكيرات" : "Enable Reminders"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {isRTL 
                      ? "إرسال تذكيرات قبل 30 و10 دقائق" 
                      : "Send reminders 30 & 10 min before"}
                  </p>
                </div>
              </div>
              <Switch
                checked={newSpace.enableReminders}
                onCheckedChange={(checked) => setNewSpace({
                  ...newSpace,
                  enableReminders: checked
                })}
              />
            </div>

            {/* Live Toggle */}
            <div className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2">
                <Radio className="w-4 h-4 text-red-600" />
                <div>
                  <p className="text-sm font-medium">
                    {isRTL ? "بث مباشر الآن" : "Live Broadcast Now"}
                  </p>
                </div>
              </div>
              <Switch
                checked={newSpace.is_live}
                onCheckedChange={(checked) => setNewSpace({
                  ...newSpace,
                  is_live: checked
                })}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => {
                setIsAddDialogOpen(false);
                resetNewSpace();
              }}>
                {isRTL ? "إلغاء" : "Cancel"}
              </Button>
              <Button onClick={handleAddSpace} disabled={addMutation.isPending}>
                {addMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {isRTL ? "جاري الإضافة..." : "Adding..."}
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    {isRTL ? "إضافة المساحة" : "Add Space"}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AdminPageBackground>
  );
};

export default XSpacesAdmin;
